import React, { useEffect, useState } from 'react';
import { Button, Table, Container, Row, Col, Form, Modal } from 'react-bootstrap';
import Select from 'react-select';
import EditProductModal from './EditProducModal';
import DuplicateProductModal from './DuplicateProductModal';
import { BASE_URL } from '../config';
// Add Bootstrap icons for our buttons
// import 'bootstrap-icons/font/bootstrap-icons.css';

// The styles have been moved to ProductList.css
// You can now use className="react-select" for the base select
// and className="filter-select" for the filter selects

const HIDDEN_COLUMNS = new Set(['date', 'sold_date', 'is_sold', 'shop', 'order']);

function ProductList() {
  const [products, setProducts] = useState([]);
  const [editProduct, setEditProduct] = useState(null);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({});
  const [filterConfigs, setFilterConfigs] = useState({});
  const [warehouses, setWarehouses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [duplicateProductData, setDuplicateProductData] = useState(null);
  const [productToMarkSold, setProductToMarkSold] = useState(null);
  const [isMarkingSold, setIsMarkingSold] = useState(false);
  const [confirmSoldStep2, setConfirmSoldStep2] = useState(false);
  const [sortBy, setSortBy] = useState('none');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sortFromDate, setSortFromDate] = useState('');

  // Wyszukiwarka tekstowa
  const [searchQuery, setSearchQuery] = useState('');

  // Pobieranie danych z API
  async function fetchData() {
    const [
      columnsResponse,
      productsResponse,
      filterResponse,
      warehousesResponse,
      locationsResponse,
    ] = await Promise.all([
      fetch(`${BASE_URL}/api/columns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }),
      fetch(`${BASE_URL}/api/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      }),
      fetch(`${BASE_URL}/api/filters`, { method: 'GET' }),
      fetch(`${BASE_URL}/api/warehouses`, { method: 'GET' }),
      fetch(`${BASE_URL}/api/locations`, { method: 'GET' })
    ]);

    const columnsData = await columnsResponse.json();
    const productsData = await productsResponse.json();
    const filterData = await filterResponse.json();
    const warehousesData = await warehousesResponse.json();
    const locationsData = await locationsResponse.json();

    setColumns(columnsData);
    const columnsToShow = columnsData
      .filter(col => !HIDDEN_COLUMNS.has(col.column_name))
      .map(col => col.column_name);
    setSelectedColumns(columnsToShow);
    setProducts(productsData);
    setFilterConfigs(filterData);
    setWarehouses(warehousesData);
    setLocations(locationsData);
  }

  useEffect(() => {
    fetchData();
  }, []);

  // Obsługa pola wyszukiwania
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Funkcja łącząca filtry z wyszukiwarką
  const filterProducts = (product) => {
    // Najpierw standardowe filtry z selectów
    const matchesFilters = Object.keys(filters).every((column) => {
      if (!filters[column]?.length) return true;
      return filters[column].some((value) => {
        const productValue = product[column];
        
        
        // For size and other categorical fields, use exact matching
        if (column === 'size' || column === 'color' || column === 'brand' || column === 'warehouse' || column === 'location') {
          if (typeof productValue === 'string') {
            // Exact matching (case insensitive)
            return productValue.toLowerCase() === value.toLowerCase();
          }
        }
        
        // For other string fields, use includes matching as before
        if (typeof productValue === 'string') {

          return productValue.toLowerCase().includes(value.toLowerCase());
        }
        if (typeof productValue === 'number') {
          return productValue === parseFloat(value);
        }
        return false;
      });
    });

    // Wyszukiwanie w pozostałych polach
    const matchesSearch = Object.values(product).some(value =>
      value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    );

    return matchesFilters && matchesSearch;
  };

  // Obsługa modali
  const openEditModal = (product) => setEditProduct(product);
  const openDuplicateModal = (product) => setDuplicateProductData(product);

  const handleCreateProduct = async (newProduct) => {
    try {
      const response = await fetch(`${BASE_URL}/api/add_product`, {
        method: 'POST',
        body: newProduct  // newProduct jako FormData
      });
      if (!response.ok) {
        throw new Error('Error creating product');
      }
      setDuplicateProductData(null);
      await fetchData();
      alert('Produkt został pomyślnie utworzony');
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Wystąpił błąd podczas tworzenia produktu');
    }
  };

  const deleteProduct = async (id) => {
    const confirm = window.confirm('Czy na pewno chcesz usunąć ten produkt?');
    if (!confirm) return;

    const response = await fetch(`${BASE_URL}/api/delete/${id}`, { method: 'DELETE' });
    if (response.ok) {
      setProducts(prev => prev.filter(product => product.id !== id));
    } else {
      alert('Error deleting product');
    }
  };

  const handleSaveChanges = async (updatedProduct) => {
    const response = await fetch(`${BASE_URL}/api/edit/${updatedProduct.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedProduct)
    });
    if (response.ok) {
      setEditProduct(null);
      fetchData();
    } else {
      alert('Error updating product');
    }
  };

  const markAsSold = async (productId) => {
    if (!productId) {
      alert('Nie udało się oznaczyć produktu jako sprzedanego (brak ID).');
      return;
    }

    setIsMarkingSold(true);
    try {
      const response = await fetch(`${BASE_URL}/api/mark-as-sold/${productId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        setProductToMarkSold(null);
        await fetchData(); // Refresh the products list
      } else {
        alert('Błąd podczas oznaczania produktu jako sprzedany');
      }
    } catch (error) {
      console.error('Error marking product as sold:', error);
      alert('Wystąpił błąd podczas oznaczania produktu jako sprzedany');
    } finally {
      setIsMarkingSold(false);
    }
  };

  // Obsługa zmiany widocznych kolumn
  const handleColumnChange = (selectedOptions) => {
    setSelectedColumns(selectedOptions.map(option => option.value));
  };

  // Obsługa zmian w filtrach
  const handleFilterChange = (column, values) => {
    setFilters({
      ...filters,
      [column]: values
    });
  };

  // Renderowanie pól do filtrowania w nagłówku tabeli
  const renderFilterInput = (column) => {
    const filterConfig = filterConfigs[column.column_name];
    if (!filterConfig) return null;

    // Helper function to format size values more compactly
    const formatOptionLabel = (option, { context }) => {
      // If it's the size column, make the display more compact
      if (column.column_name === 'size') {
        return context === 'value' ? 
          // For selected values in the input
          <div style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>{option.label}</div> : 
          // For options in the dropdown
          <div className="size-filter-option">{option.label}</div>;
      }
      return option.label;
    };

    switch (filterConfig.type) {
      case 'text':
      case 'select':
        return (
          <Select
            isMulti
            options={filterConfig.options.map(option => ({ value: option, label: option }))}
            value={filters[column.column_name]?.map(value => ({ value, label: value })) || []}
            onChange={selectedOptions =>
              handleFilterChange(column.column_name, selectedOptions.map(option => option.value))
            }
            className="react-select"
            formatOptionLabel={column.column_name === 'size' ? formatOptionLabel : undefined}
            placeholder={`Wybierz...`}
            noOptionsMessage={() => "Brak opcji"}
            isClearable={true}
            isSearchable={true}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            className="form-control form-control-sm mt-1"
            value={filters[column.column_name] || ''}
            onChange={e => handleFilterChange(column.column_name, e.target.value)}
            placeholder={`Wprowadź ${column.column_display.toLowerCase()}...`}
          />
        );
      case 'magazyn':
        return (
          <Select
            isMulti
            options={warehouses.map(warehouse => ({ value: warehouse.id, label: warehouse.name }))}
            value={
              filters[column.column_name]?.map(value => ({
                value,
                label: warehouses.find(wh => wh.id === value)?.name
              })) || []
            }
            onChange={selectedOptions =>
              handleFilterChange(column.column_name, selectedOptions.map(option => option.value))
            }
            className="react-select"
            placeholder="Wybierz magazyn..."
            noOptionsMessage={() => "Brak magazynów"}
            isClearable={true}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        );
      case 'lokalizacja':
        return (
          <Select
            isMulti
            options={locations.map(location => ({ value: location.location_id, label: location.name }))}
            value={
              filters[column.column_name]?.map(value => ({
                value,
                label: locations.find(loc => loc.location_id === value)?.name
              })) || []
            }
            onChange={selectedOptions =>
              handleFilterChange(column.column_name, selectedOptions.map(option => option.value))
            }
            className="react-select"
            placeholder="Wybierz lokalizację..."
            noOptionsMessage={() => "Brak lokalizacji"}
            isClearable={true}
            menuPortalTarget={document.body}
            menuPosition={'fixed'}
            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
          />
        );
      default:
        return null;
    }
  };

  // Obliczanie sum i sortowanie dla widocznych (filtrowanych) produktów
  const visibleProducts = products.filter(filterProducts);

  const productsAfterSelectedDate = sortBy === 'date' && sortFromDate
    ? visibleProducts.filter((product) => {
        if (!product?.date) return false;
        const productDate = new Date(product.date);
        if (Number.isNaN(productDate.getTime())) return false;
        const selectedDate = new Date(`${sortFromDate}T00:00:00`);
        return productDate >= selectedDate;
      })
    : visibleProducts;

  const sortedVisibleProducts = [...productsAfterSelectedDate].sort((a, b) => {
    const direction = sortDirection === 'asc' ? 1 : -1;

    if (sortBy === 'date') {
      const dateA = a?.date ? new Date(a.date).getTime() : 0;
      const dateB = b?.date ? new Date(b.date).getTime() : 0;
      return (dateA - dateB) * direction;
    }

    if (sortBy === 'name') {
      const nameA = (a?.name || '').toString();
      const nameB = (b?.name || '').toString();
      return nameA.localeCompare(nameB, 'pl', { sensitivity: 'base' }) * direction;
    }

    if (sortBy === 'sku') {
      const skuA = (a?.sku || '').toString();
      const skuB = (b?.sku || '').toString();
      return skuA.localeCompare(skuB, 'pl', { numeric: true, sensitivity: 'base' }) * direction;
    }

    return 0;
  });

  const parsePrice = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    // Zamiana przecinka na kropkę jeśli występuje
    const normalized = typeof val === 'string' ? val.replace(',', '.') : val;
    const parsed = parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const totalSalePrice = sortedVisibleProducts.reduce((sum, p) => sum + parsePrice(p.sale_price), 0);
  const totalBuyPrice = sortedVisibleProducts.reduce((sum, p) => sum + parsePrice(p.buy_price), 0);
  const totalConsignmentPrice = sortedVisibleProducts.reduce((sum, p) => sum + parsePrice(p.consignment_price), 0);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleString('pl-PL');
  };

  const getStickyLeft = (columnName) => {
    const ID_W = 36;
    const IMAGE_W = 120;
    const SKU_W = 130;
    if (columnName === 'image') return ID_W;
    if (columnName === 'sku') return ID_W + (selectedColumns.includes('image') ? IMAGE_W : 0);
    if (columnName === 'name') return ID_W + (selectedColumns.includes('image') ? IMAGE_W : 0) + (selectedColumns.includes('sku') ? SKU_W : 0);
    return null;
  };

  const getColumnClassName = (columnName) => {
    const classes = [];

    if (columnName === 'image') classes.push('image-column');
    if (columnName.includes('price')) classes.push('price-column');
    if (columnName === 'note') classes.push('note-column');
    if (['shop', 'order', 'ksfv', 'is_sold', 'size'].includes(columnName)) classes.push('compact-column');
    if (columnName === 'sku') classes.push('sku-column');
    if (['date', 'sold_date'].includes(columnName)) classes.push('date-column');
    if (columnName === 'name') classes.push('name-column');
    if (columnName === 'category') classes.push('wide-text-column');
    if (['location', 'warehouse'].includes(columnName)) classes.push('medium-text-column');

    return classes.join(' ');
  };

  const currencyFormatter = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });

  return (
    <Container fluid className="my-4">
      {/* SEKCJA: WYSZUKIWARKA + POLE WYBORU KOLUMN */}
      <Row className="mb-4 g-3 control-panel">
        {/* Wyszukiwarka */}
        <Col xs={12} md={4}>
          <div className="search-wrapper position-relative">
            <i className="bi bi-search position-absolute" style={{ left: '15px', top: '12px' }}></i>
            <Form.Control
              type="text"
              placeholder="Szukaj..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
        </Col>
        {/* Wybór kolumn do wyświetlenia */}
        <Col xs={12} md={5}>
          <div className="d-flex align-items-center w-100">
            <i className="bi bi-columns-gap me-2 text-primary"></i>
            <Select
              isMulti
              placeholder="Wybierz kolumny..."
              options={columns
                .filter(col => !HIDDEN_COLUMNS.has(col.column_name))
                .map(column => ({
                  value: column.column_name,
                  label: column.column_display
                }))}
              value={columns
                .filter(column => selectedColumns.includes(column.column_name))
                .map(col => ({
                  value: col.column_name,
                  label: col.column_display
                }))}
              onChange={handleColumnChange}
              className="react-select w-100"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              menuPosition={'fixed'}
              styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
            />
          </div>
        </Col>

        {/* Sortowanie */}
        <Col xs={12} md={3}>
          <div className="d-flex flex-column gap-2">
            <div className="d-flex gap-2 align-items-center">
              <i className="bi bi-sort-down text-primary"></i>
              <Form.Select
                value={sortBy}
                onChange={(e) => {
                  const nextSortBy = e.target.value;
                  setSortBy(nextSortBy);
                  if (nextSortBy !== 'date') {
                    setSortFromDate('');
                  }
                }}
                size="sm"
                aria-label="Sortowanie według"
              >
                <option value="none">Bez filtrowania</option>
                <option value="date">Data</option>
                <option value="name">Nazwa produktu</option>
                <option value="sku">Numer zamówienia (SKU)</option>
              </Form.Select>

              {sortBy !== 'none' && (
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))}
                  title={sortDirection === 'asc' ? 'Rosnąco' : 'Malejąco'}
                >
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Button>
              )}
            </div>

            {sortBy === 'date' && (
              <div className="d-flex justify-content-end">
                <Form.Control
                  type="date"
                  size="sm"
                  value={sortFromDate}
                  onChange={(e) => setSortFromDate(e.target.value)}
                  aria-label="Pokaż produkty od wybranej daty"
                  style={{ maxWidth: '190px', borderRadius: '10px', fontSize: '0.85rem' }}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col xs={12}>
          <div className="small text-muted">
            Sortowanie: <strong>{sortBy === 'none' ? 'Bez filtrowania' : sortBy === 'date' ? 'Data' : sortBy === 'name' ? 'Nazwa produktu' : 'Numer zamówienia (SKU)'}</strong>
            {sortBy !== 'none' ? <> , kierunek: <strong>{sortDirection === 'asc' ? 'rosnąco' : 'malejąco'}</strong></> : null}
            {sortFromDate ? <> , od daty: <strong>{sortFromDate}</strong></> : null}
            , rekordów: <strong>{sortedVisibleProducts.length}</strong>
          </div>
        </Col>
      </Row>

      {/* Totals: suma cen sprzedaży i zakupu dla widocznych produktów - pod wyszukiwarką */}
      <Row className="mb-3">
        <Col xs={12} md={4} className="mb-2">
          <div className="p-2 border rounded d-flex justify-content-between align-items-center">
            <strong>Suma ceny sprzedaży:</strong>
            <span>{currencyFormatter.format(totalSalePrice)}</span>
          </div>
        </Col>
        <Col xs={12} md={4} className="mb-2">
          <div className="p-2 border rounded d-flex justify-content-between align-items-center">
            <strong>Suma ceny zakupu:</strong>
            <span>{currencyFormatter.format(totalBuyPrice)}</span>
          </div>
        </Col>
        <Col xs={12} md={4} className="mb-2">
          <div className="p-2 border rounded d-flex justify-content-between align-items-center">
            <strong>Suma ceny w komisie:</strong>
            <span>{currencyFormatter.format(totalConsignmentPrice)}</span>
          </div>
        </Col>
      </Row>

      {/* Tabela */}
      <div className="table-responsive table-scroll-x" style={{ marginBottom: '80px' }}>
        <Table striped hover bordered className="wide-table">
          <thead>
            <tr>
              <th className="id-column text-center">#</th>
              {columns.map((column) =>
                selectedColumns.includes(column.column_name) && (
                  <th
                    key={column.column_name}
                    className={getColumnClassName(column.column_name)}
                    style={getStickyLeft(column.column_name) !== null ? { left: getStickyLeft(column.column_name) } : undefined}
                  >
                    <div className="d-flex flex-column position-relative">
                      <span className="text-primary mb-1">{column.column_display}</span>
                      {renderFilterInput(column)}
                    </div>
                  </th>
                )
              )}
              <th className="actions-column text-center">Wykonaj</th>
            </tr>
          </thead>
          <tbody>
            {sortedVisibleProducts.map((product, index) => (
              <tr key={product.id}>
                <td className="id-column">{index + 1}</td>
                {columns.map((column) =>
                  selectedColumns.includes(column.column_name) && (
                    <td
                      key={column.column_name}
                      className={getColumnClassName(column.column_name)}
                      style={{
                        textAlign:
                          column.column_name === 'buy_price' || column.column_name === 'sale_price' || column.column_name === 'consignment_price'
                            ? 'right'
                            : 'center',
                        ...(getStickyLeft(column.column_name) !== null ? { left: getStickyLeft(column.column_name) } : {})
                      }}
                    >
                      {column.column_name === 'image' ? (
                        <img
                        src={`${BASE_URL}/static/media/${product[column.column_name]}`}
                        alt="Product"
                        style={{
                          width: 'auto',
                          height: '100px',
                          display: 'block',
                          margin: '0 auto'
                        }}
                      />
                      ) : column.column_name === 'is_sold' ? (
                        product[column.column_name] ? 'Tak' : 'Nie'
                      ) : column.column_name === 'date' || column.column_name === 'sold_date' ? (
                        formatDateTime(product[column.column_name])
                      ) : typeof product[column.column_name] === 'string' ? (
                        <div className="truncate-text">
                          {product[column.column_name]}
                        </div>
                      ) : (
                        product[column.column_name]
                      )}
                    </td>
                  )
                )}
                <td className="actions-column">
                  <Row className="g-1">
                    <Col xs={12}>
                      <div className="d-flex gap-1 mb-1">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => openEditModal(product)}
                          className="flex-grow-1 w-50 d-flex align-items-center justify-content-center"
                        >
                          <i className="bi bi-pencil-square me-1"></i> Edytuj
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => deleteProduct(product.id)}
                          className="flex-grow-1 w-50 d-flex align-items-center justify-content-center"
                        >
                          <i className="bi bi-trash me-1"></i> Usuń
                        </Button>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div className="d-flex gap-1">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openDuplicateModal(product)}
                          className="flex-grow-1 w-50 d-flex align-items-center justify-content-center"
                        >
                          <i className="bi bi-copy me-1"></i> Powiel
                        </Button>
                        {!product.is_sold ? (
                          <Button
                            variant="success"
                            size="sm"
                            onClick={() => setProductToMarkSold(product)}
                            className="flex-grow-1 w-50 d-flex align-items-center justify-content-center"
                          >
                            <i className="bi bi-bag-check me-1"></i> Sprzedane
                          </Button>
                        ) : (
                          <div className="flex-grow-1 w-50"></div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Modale */}
      {editProduct && (
        <EditProductModal
          show={Boolean(editProduct)}
          product={editProduct}
          onHide={() => setEditProduct(null)}
          onSave={handleSaveChanges}
        />
      )}
      {duplicateProductData && (
        <DuplicateProductModal
          show={Boolean(duplicateProductData)}
          product={duplicateProductData}
          onHide={() => setDuplicateProductData(null)}
          onCreate={handleCreateProduct}
          
        />
      )}

      <Modal
        show={Boolean(productToMarkSold) && !confirmSoldStep2}
        onHide={() => { if (!isMarkingSold) { setProductToMarkSold(null); setConfirmSoldStep2(false); } }}
        centered
        size="sm"
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Potwierdzenie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Czy oznaczyć ten produkt jako sprzedany?
          {productToMarkSold && (
            <div className="mt-2 text-muted" style={{ fontSize: '0.9rem' }}>
              {[productToMarkSold.brand, productToMarkSold.model].filter(Boolean).join(' ')}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setProductToMarkSold(null)}
            disabled={isMarkingSold}
          >
            Anuluj
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={() => setConfirmSoldStep2(true)}
            disabled={isMarkingSold}
          >
            Tak, sprzedane
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmSoldStep2}
        onHide={() => !isMarkingSold && setConfirmSoldStep2(false)}
        centered
        size="sm"
        animation={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">Ostateczne potwierdzenie</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <strong>Na pewno?</strong> Produkt zostanie przeniesiony do SPRZEDANE i nie będzie widoczny na liście.
          {productToMarkSold && (
            <div className="mt-2 text-muted" style={{ fontSize: '0.9rem' }}>
              {[productToMarkSold.brand, productToMarkSold.model].filter(Boolean).join(' ')}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setConfirmSoldStep2(false)}
            disabled={isMarkingSold}
          >
            Cofnij
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => { setConfirmSoldStep2(false); markAsSold(productToMarkSold?.id); }}
            disabled={isMarkingSold}
          >
            {isMarkingSold ? 'Zapisywanie...' : 'Potwierdź sprzedaż'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ProductList;